<template>
    <InputDropdown
        ref="inputDropdown"
        :items="items"
        :item-label="getLabel"
        :item-value="getValue"
        :error="error"
        :disabled="disabled"
        :multiple="multiple"
        :multiline="multiline"
        :options-loading="optionsLoading"
        key-field="countryCode"
        v-bind="bindings"
        @update:model-value="emit('update:modelValue', $event)"
        @clear="emit('clear')"
        @dropdown-open="onDropdownOpen"
    >
        <template #dropdown-icon>
            <LoadingCircle v-if="optionsLoading" />
        </template>

        <template #dropdown-selected-option="{ selectedItemLabel, selectedItemValue }">
            <div
                v-if="selectedItemValue"
                class="value-container"
            >
                <CountryFlag
                    v-if="displayCountryFlags && selectedItemValue && !multiple"
                    :country-code="selectedItemValue"
                />
                <Words
                    :oneline="!multiline"
                    :class="{ 'text-break': selectedItemValue }"
                >{{ selectedItemLabel }}</Words>
            </div>
        </template>

        <template #dropdown-option="{ item }">
            <div class="value-container option-container">
                <CountryFlag
                    v-if="displayCountryFlags"
                    :country-code="getValue(item)"
                />
                <Words
                    :oneline="!multiline"
                    class="text-break"
                >{{ getLabel(item) }}</Words>
            </div>
        </template>
    </InputDropdown>
</template>

<script setup lang="ts">
import { omit } from 'ramda'
import { computed, inject, ref } from 'vue'
import InputDropdown from 'widget-components/components/InputDropdown/InputDropdown.vue'
import { lazyBlockToolsInjectionKey } from 'widget-components/integrations/CustomPage/injection-keys'
import type { BlockPath, InputCountry, InputCountryItem, InputDropdownItem, LazyBlockToolsInject } from 'widget-components/integrations/CustomPage/types'
import CountryFlag from './CountryFlag.vue'
import LoadingCircle from '../LoadingCircle/LoadingCircle.vue'
import type { InputDropdownProps } from '../InputDropdown/types'
import Words from 'widget-components/components/Words/Words.vue'

const getLabel = (item: InputCountryItem) => item.label!
const getValue = (item: InputCountryItem) => item.countryCode!

defineOptions({ inheritAttrs: false })

const props = defineProps<{
    id: string
    modelValue?: InputDropdownItem | InputDropdownItem[]
    initialValue?: InputDropdownItem | InputDropdownItem[]
    error?: string | boolean
    disabled?: boolean
    items?: InputDropdownItem[]
    selectedItems?: InputDropdownItem[]
    label?: string
    multiple?: boolean
    multiline?: boolean
    displayCountryFlags?: boolean
    showChips?: boolean
    itemLabel?: () => string
    itemValue?: () => InputDropdownItem
    blockPath?: BlockPath
    placeholder?: string
    focus?: boolean
}>()

const inputDropdown = ref<InstanceType<typeof InputDropdown>>()

const {
    requestBlockConfig,
    getLazyBlockProp,
} = inject(lazyBlockToolsInjectionKey, {
    requestBlockConfig: () => {},
    getLazyBlockProp: () => {} ,
    dispatchComponentAction: async () => {},
} as LazyBlockToolsInject)

const error = computed(() => props.error || getLazyBlockProp<string>(props.id, 'error'))

const optionsLoading = computed(() => getLazyBlockProp<boolean>(props.id, 'loading'))

const items = computed(() => {
    const lazyItems = getLazyBlockProp<InputCountry[] | undefined>(props.id || 'no-id', 'items')

    if (lazyItems?.length) {
        return lazyItems
    }

    if (props.selectedItems?.length) {
        return props.selectedItems
    }

    return props.items || []
})

const emit = defineEmits(['update:modelValue', 'clear'])

const bindings = computed<Partial<InputDropdownProps>>(() => ({
    ...omit(['items', 'error', 'disabled', 'selectedItems', 'displayCountryFlags', 'multiple', 'itemLabel', 'itemValue'], props),
}))

const onDropdownOpen = async () => {
    await requestBlockConfig({ id: props.id || 'no-id', type: 'INPUT_COUNTRY' })
    inputDropdown.value?.updateDropdownOverlay()
    inputDropdown.value?.focusSearch()
}


</script>

<style lang="sass" scoped>
@import './InputCountry.sass'
</style>
